import SchoolCollegeJson from "./SchoolCollege.json";

/**
 * School / College lookup for the Enquiry form.
 *
 * The list is filtered by the District, Taluka, Village and Qualification the
 * applicant has already selected.
 *
 * Two things make this less direct than a plain equality check:
 *
 * 1. Taluka spellings differ between the client sheet and the address API that
 *    powers the Tehsil/Taluka dropdown (sheet "VANSDA" vs API "Vansada",
 *    "CHORYASI" vs "Chorasi", ...). Each record therefore carries the API
 *    spelling in `t`, with the original sheet spelling kept in `ts`, and both
 *    are accepted when matching.
 *
 * 2. The sheet's village column is only partly made up of real village names -
 *    it also holds landmarks ("GIDC VAPI", "KUMAR SHALA NO.1", "MUKHYA
 *    KANYASHALA"), so roughly half of the rows have no counterpart in the
 *    Village dropdown. Village is consequently used to *narrow* the list and
 *    never to empty it: if nothing in the taluka matches the chosen village we
 *    fall back to every school in that taluka. See `getSchoolOptions`.
 */

export const OTHER_SCHOOL_VALUE = "OTHER (NOT LISTED)";

const QUAL_CODES = SchoolCollegeJson.qualCodes || {};
const SCHOOLS = SchoolCollegeJson.schools || [];

// "10TH PASS" -> "10", "GRADUATE" -> "G", ...
const QUAL_TO_CODE = Object.entries(QUAL_CODES).reduce((acc, [code, label]) => {
  acc[label] = code;
  return acc;
}, {});

const normalize = (value) =>
  `${value || ""}`.toUpperCase().replace(/[^A-Z0-9]/g, "");

// "Bartad (khanpur)" and "BARTAD" should be treated as the same place.
const normalizeBase = (value) =>
  normalize(`${value || ""}`.replace(/\(.*?\)/g, ""));

// The District dropdown is fed by the address API, which does not always spell a
// district the way the client sheet does - it offers "Dangs" where the sheet says
// "DANG". Each pair below maps one of those API spellings onto the sheet's.
const DISTRICT_ALIASES = {
  DANGS: "DANG",
};

const matchesDistrict = (school, district) => {
  const target = normalize(district);
  const alias = DISTRICT_ALIASES[target];
  const schoolDistrict = normalize(school.d);
  return (
    schoolDistrict === target || (alias && schoolDistrict === normalize(alias))
  );
};

const matchesTaluka = (school, taluka) => {
  const target = normalize(taluka);
  if (!target) return false;
  return normalize(school.t) === target || normalize(school.ts) === target;
};

// Some villages are spelled one way in the client sheet and another in the
// address master, where the two are the same place transliterated differently
// (Gujarati Z/J, V/W, doubled vowels). Only pairs that are provably the same
// place belong here - a merely similar name is a different village.
//
// Keyed by the address master's spelling, which is what the Village dropdown
// offers, mapping to the spelling the sheet uses.
const VILLAGE_ALIASES = {
  JHAGADIA: "ZAGHADIA",
  JHAVDA: "ZAVADA",
  AT: "AAT",
  "SANIYA HEMAD": "SHANIYA-AHMED",
  "AMBA JUNGLE": "AMBEJUNGAL",
};

const matchesVillage = (school, village) => {
  const target = normalize(village);
  if (!target) return false;
  if (normalize(school.v) === target) return true;
  if (normalizeBase(school.v) === normalizeBase(village)) return true;

  const alias = VILLAGE_ALIASES[`${village || ""}`.trim().toUpperCase()];
  return Boolean(alias) && normalize(school.v) === normalize(alias);
};

const matchesQualification = (school, qualification) => {
  if (!qualification) return true;
  const code = QUAL_TO_CODE[`${qualification}`.toUpperCase()];
  // Qualifications with no schooling equivalent (ITI, DIPLOMA, NA, ...) are not
  // present in the sheet, so they must not filter the list down to nothing.
  if (!code) return true;
  return Array.isArray(school.q) && school.q.includes(code);
};

const toOption = (name) => ({ label: name, value: name });

const sortByName = (a, b) => a.localeCompare(b);

/**
 * The village values the client sheet uses for a given district and taluka.
 *
 * The Village dropdown is fed by the address master, which is built from India
 * Post records and so only lists places that host a post office. Villages
 * without one are missing entirely - Saputara, a well-known hill station, is
 * not in it - and the sheet's column is headed "Village Name or Land Mark", so
 * it also holds legitimate non-village locations such as GIDC VAPI.
 *
 * Offering these alongside the address-master villages lets an applicant pick
 * the value their school is actually filed under, instead of falling back to
 * every school in the taluka.
 */
export const getSheetVillages = ({ district, taluka } = {}) => {
  if (!district || !taluka) return [];

  const villages = SCHOOLS.filter(
    (school) =>
      matchesDistrict(school, district) &&
      matchesTaluka(school, taluka) &&
      school.v
  ).map((school) => `${school.v}`.toUpperCase());

  return [...new Set(villages)].sort(sortByName);
};

/**
 * Build the School / College dropdown options.
 *
 * District + Taluka are required; Qualification and Village refine the result.
 * An "OTHER (NOT LISTED)" entry is always appended so an applicant whose school
 * is missing from the sheet - every Daman school, for instance - is never
 * blocked.
 */
export const getSchoolOptions = ({
  district,
  taluka,
  village,
  qualification,
} = {}) => {
  const otherOption = toOption(OTHER_SCHOOL_VALUE);

  if (!district || !taluka) return [otherOption];

  const inTaluka = SCHOOLS.filter(
    (school) =>
      matchesDistrict(school, district) && matchesTaluka(school, taluka)
  );

  const byQualification = inTaluka.filter((school) =>
    matchesQualification(school, qualification)
  );

  // Village narrows the list, but only when it actually matches something -
  // otherwise the applicant would be left with an empty dropdown.
  const byVillage = village
    ? byQualification.filter((school) => matchesVillage(school, village))
    : [];

  const matched = byVillage.length ? byVillage : byQualification;

  const names = [...new Set(matched.map((school) => school.n))].sort(sortByName);

  return [...names.map(toOption), otherOption];
};

export default getSchoolOptions;
